import * as echarts from 'echarts';
import get from 'lodash/get';
import set from 'lodash/set';
import debounce from 'lodash/debounce';
import chartGaugeMixin from './gauge/chart-gauge.mixin';
import chartCustomMixin from './custom/chart-custom.mixin';
import chartLineMixin from './line-bar/chart-line.mixin';
import chartBarMixin from './line-bar/chart-bar.mixin';
import chartHeatmapMixin from './heatmap/chart-heatmap.mixin';
export default {
  name: 'DashboardChart',
  mixins: [chartGaugeMixin, chartCustomMixin, chartLineMixin, chartBarMixin, chartHeatmapMixin],
  props: {
    widget: {
      type: Object,
      required: true
    },
    data: {
      type: Object,
      required: false,
      default: () => ({})
    },
    dateRange: {
      type: Object,
      required: true
    },
    displayAsJson: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data() {
    return {
      chart: null,
      elResizeObserver: null,
      renderCleanupCacheKey: '',
      showChartCanvas: true,
      chartOptionsJson: null,
      lastWidth: null,
      lastHeight: null
    };
  },
  computed: {
    chartData() {
      if (!this.data) {
        return [];
      }
      return this.data.chart || [];
    },
    chartType() {
      return get(this.widget, 'chartOptions.type');
    },
    isDarkTheme() {
      return this.$vuetify.theme.isDark;
    },
    hasValidData() {
      if (this.chartType === 'gauge') {
        const dataSets = this.getGaugeChartDataSets();
        return dataSets && dataSets.length > 0;
      }
      return true;
    }
  },
  watch: {
    widget: {
      handler() {
        this.renderChart();
      }
      // deep: true,
    },
    chartData: {
      handler() {
        this.renderChart();
        this.hideLimitReachedAlert = false;
      },
      deep: true
    },
    isDarkTheme() {
      this.renderChart();
    }
  },
  mounted() {
    this.elResizeObserver = new ResizeObserver(debounce(this.resize.bind(this), 300));
    this.elResizeObserver.observe(this.$refs.chartContainer);
  },
  updated() {
    this.chart.resize();
  },
  beforeDestroy() {
    if (this.chart) {
      this.chart.dispose();
    }
    if (this.elResizeObserver) {
      this.elResizeObserver.disconnect();
    }
  },
  methods: {
    // eslint-disable-next-line max-statements
    renderChart(needsCleanup = false) {
      if (!this.$refs.chartCanvas) {
        console.error('no chart canvas');
        return;
      }
      if (!this.chart) {
        this.chart = echarts.init(this.$refs.chartCanvas);
      }
      if (!this.chartType) {
        console.warn('No chart type found in widget', this.widget);
        return;
      }
      let options;
      try {
        switch (this.chartType) {
          case 'custom':
            options = this.getCustomChartOptions();
            if (!options.title) {
              options.title = {
                text: '',
                subtext: ''
              };
            }
            break;
          case 'bar':
            options = this.getBarChartOptions();
            break;
          case 'line':
            options = this.getLineChartOptions();
            break;
          case 'gauge':
            options = this.getGaugeChartOptions();
            break;
          case 'heatmap':
            options = this.getHeatmapChartOptions();
            break;
          default:
            throw new Error(`Invalid Chart type: ${this.chartType}`);
        }
        if (options.toolbox === undefined) {
          options.toolbox = {
            feature: {}
          };
          const toolboxActions = get(this.widget, 'chartOptions.toolbox.actions', []);
          toolboxActions.forEach(action => {
            options.toolbox.feature[action] = {};
          });
        }
        if (get(options, 'legend') && !get(options, 'legend.textStyle.color')) {
          if (this.isDarkTheme) {
            set(options, 'legend.textStyle.color', '#eee');
          } else {
            set(options, 'legend.textStyle.color', '#333');
          }
        }
        if (!get(this.widget, 'chartOptions.legend.show', true)) {
          options.legend = {
            show: false
          };
        }
        const series = get(options, 'series', []);
        const newRenderCleanupCacheKey = JSON.stringify({
          chartType: this.chartType,
          toolboxActions: get(this.widget, 'chartOptions.toolbox.actions', []),
          seriesCount: series.length,
          seriesNames: series.map(series => series.name),
          showLegend: get(this.widget, 'chartOptions.legend.show', true),
          legend: get(this.widget, 'chartOptions.legend', {})
        });
        let notMerge = false;
        if (needsCleanup || this.renderCleanupCacheKey !== newRenderCleanupCacheKey) {
          notMerge = true;
        }
        this.renderCleanupCacheKey = newRenderCleanupCacheKey;
        this.chartOptionsJson = options;
        if (!this.displayAsJson) {
          this.chart.setOption(options, {
            notMerge,
            lazyUpdate: true
          });
        }
      } catch (error) {
        console.error('Error in chart options', error);
        this.chart.clear();
        this.chart.setOption({
          title: {
            text: this.$t('Dashboards.components.chart.errorInGenerator.text'),
            subtext: this.$t('Dashboards.components.chart.errorInGenerator.subtext', {
              error: String(error)
            }),
            left: 'center',
            textStyle: {
              color: 'red'
            }
          }
        });
      }
    },
    resize() {
      const el = this.$refs.chartContainer;
      const width = Math.round(el.offsetWidth);
      const height = Math.round(el.offsetHeight);
      if (width === this.lastWidth) {
        return;
      }
      this.lastWidth = width;
      this.lastHeight = height;
      if (this.displayAsJson && this.chartOptionsJson) {
        return;
      }
      if (!this.chart) {
        return this.renderChart(true);
      }
      this.chart.resize();
    },
    getStateLabel(twin, state, value) {
      twin = twin.twin || twin.virtualDevice;
      let defaultTemplate = '{{state.title}}';
      if (this.chartData.length > 1) {
        defaultTemplate = '{{twin.title}} - {{state.title}}';
      }
      if (this.chartType === 'gauge' && value !== undefined) {
        defaultTemplate += ' ({{value}})';
      }
      let template = get(this.widget, 'chartOptions.labelFormat') || defaultTemplate;
      const placeholders = ['twin.title', 'state.key', 'value', 'state.title'];
      placeholders.forEach(placeholder => {
        let finalPlaceholder = placeholder;
        if (placeholder === 'state.title') {
          finalPlaceholder = 'state.properties.title';
        }
        const replaceValue = get({
          twin,
          state,
          value
        }, finalPlaceholder, '-');
        template = template.replace(new RegExp(`{{${placeholder}}}`, 'g'), replaceValue);
      });
      return template;
    }
  }
};